import { useEffect, useMemo, useRef, useState } from "react";
import {
  Paper,
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Tooltip
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { v5 as uuidv5 } from "uuid";
import axios from "axios";
import { supabase } from "../../supabaseClient.js";
import useAlert from "../../alert/useAlert.js";

const formatTimestamp = (ts) =>
  new Date(ts).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true
  });

const AgentChatPanel = ({
  sessionKey,
  agentName,
  roleName,
  prompt,
  tasks,
  currentOrg,
  knowledgeBaseId,
  backendUrl,
  whatsappUrl,
  isTestingBot,
  memory,
  userDetails,
  onClose
}) => {
  const { setAlert } = useAlert();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const listRef = useRef(null);

  // Deterministic UUID for session_id column
  const sessionUuid = useMemo(
    () => (sessionKey ? uuidv5(sessionKey, uuidv5.URL) : null),
    [sessionKey]
  );

  const scrollToBottom = () => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  };

  const storeMessage = async (payload) => {
    if (!sessionUuid) return null;
    const { data, error: insertError } = await supabase
      .from("textmessages")
      .insert([
        {
          message: payload.message,
          isUser: payload.isUser,
          senderid: payload.senderid ?? null,
          receiverid: payload.receiverid ?? null,
          session_id: sessionUuid,
          sources: payload.sources ?? null,
          file: payload.file ?? null
        }
      ])
      .select("id, created_at, message, isUser");
    if (insertError) {
      console.error("Error saving message:", insertError);
      return null;
    }
    return data?.[0] ?? null;
  };

  const decryptMessage = async (row) => {
    const [
      messageResult,
      senderResult,
      receiverResult,
      fileResult,
      sourcesResult
    ] = await Promise.all([
      supabase.rpc("decrypt_text", { encrypted_data: row.message }),
      supabase.rpc("decrypt_text", { encrypted_data: row.senderid }),
      supabase.rpc("decrypt_text", { encrypted_data: row.receiverid }),
      row.file
        ? supabase.rpc("decrypt_text", { encrypted_data: row.file })
        : Promise.resolve({ data: null, error: null }),
      row.sources
        ? supabase.rpc("decrypt_text", { encrypted_data: row.sources })
        : Promise.resolve({ data: "[]", error: null })
    ]);

    const decrypted = {
      ...row,
      message: messageResult.data || "",
      senderid: senderResult.data || "",
      receiverid: receiverResult.data || "",
      file: fileResult.data || null
    };

    try {
      decrypted.sources = sourcesResult.data ? JSON.parse(sourcesResult.data) : [];
    } catch {
      decrypted.sources = [];
    }

    return decrypted;
  };

  const loadMessages = async () => {
    if (!sessionUuid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from("textmessages")
      .select("id, message, isUser, created_at, senderid, receiverid, file, sources")
      .eq("session_id", sessionUuid)
      .order("created_at", { ascending: true });

    if (fetchError) {
      console.error("Error fetching messages:", fetchError);
      setError("Failed to load messages.");
      setLoading(false);
      return;
    }

    try {
      const decrypted = await Promise.all((data || []).map(decryptMessage));
      setMessages(decrypted);
      setError("");
    } catch (e) {
      console.error("Error decrypting messages:", e);
      setError("Failed to decrypt messages.");
    } finally {
      setLoading(false);
      setTimeout(scrollToBottom, 50);
    }
  };

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUuid]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !sessionUuid) return;

    setSending(true);
    setInput("");

    // Optimistic user message
    const optimisticUser = {
      id: `local-${Date.now()}`,
      message: text,
      isUser: true,
      created_at: new Date().toISOString(),
      senderid: userDetails?.id ?? "user"
    };
    setMessages((prev) => [...prev, optimisticUser]);
    setTimeout(scrollToBottom, 30);
    await storeMessage({
      message: text,
      isUser: true,
      senderid: userDetails?.id ?? "user",
      receiverid: "ai-assistant"
    });

    try {
      const endpoint = isTestingBot
        ? `${whatsappUrl}/ask-agent`
        : `${backendUrl}/call_agent_knowledgebase`;

      const body = isTestingBot
        ? {
            team_id: currentOrg ? currentOrg.id : null,
            message_body: text,
            session_id: sessionKey,
            knowledgebase_id: knowledgeBaseId,
            generalPrompt: prompt,
          }
        : {
            question: text,
            session_id: sessionKey,
            roleName,
            generalPrompt: prompt,
            tasks: tasks.map((t) => t.description),
            team_id: currentOrg ? currentOrg.id : null,
            knowledgebase_id: knowledgeBaseId
          };
        
      console.log("Sending request to:", endpoint, "with body:", body);
      const response = await axios.post(endpoint, body);
      const answer = isTestingBot
        ? response.data?.answer || ""
        : response.data?.answer?.answer || "";

      const aiMsg = {
        id: `local-ai-${Date.now()}`,
        message: answer || "(No answer returned)",
        isUser: false,
        created_at: new Date().toISOString(),
        senderid: agentName || "ai-assistant"
      };
      setMessages((prev) => [...prev, aiMsg]);
      await storeMessage({
        message: aiMsg.message,
        isUser: false,
        senderid: agentName || "ai-assistant",
        receiverid: userDetails?.id ?? "user"
      });
      setError("");
    } catch (err) {
      console.error("Error getting answer:", err);
      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to get the answer.";
      setError(errMsg);

      // Store error as an AI message
      const aiErr = {
        message: `Error: ${errMsg}`,
        isUser: false,
        senderid: agentName || "ai-assistant"
      };
      setMessages((prev) => [
        ...prev,
        {
          id: `local-err-${Date.now()}`,
          ...aiErr,
          created_at: new Date().toISOString()
        }
      ]);
      await storeMessage(aiErr);
    } finally {
      setSending(false);
      setTimeout(scrollToBottom, 50);
    }
  };

  const isEmpty = !loading && messages.length === 0;

  return (
    <Paper
      elevation={3}
      sx={{ mt: 3, p: 2, borderRadius: 2, backgroundColor: "#f7f9fb" }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <ChatIcon sx={{ color: "#29C0BB" }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Chat with Agent
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadMessages} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {onClose && (
            <Tooltip title="Close chat">
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Box
        ref={listRef}
        sx={{
          height: 600,
          overflowY: "auto",
          pr: 1,
          pl: 0.5,
          pb: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {loading && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary"
            }}
          >
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography>Loading messages...</Typography>
          </Box>
        )}

        {!loading &&
          messages.map((m) => {
            const isUser = !!m.isUser;
            return (
              <Box
                key={m.id}
                sx={{
                  display: "flex",
                  justifyContent: isUser ? "flex-end" : "flex-start"
                }}
              >
                <Box
                  sx={{
                    maxWidth: "80%",
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: isUser ? "#29C0BB" : "#ffffff",
                    color: isUser ? "#fff" : "#1a1a1a",
                    boxShadow: 1
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {m.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      color: isUser ? "rgba(255,255,255,0.85)" : "text.secondary",
                      textAlign: isUser ? "right" : "left"
                    }}
                  >
                    {formatTimestamp(m.created_at || new Date())}
                  </Typography>
                </Box>
              </Box>
            );
          })}

        {sending && !loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 1,
              color: "text.secondary"
            }}
          >
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="caption">Waiting for agent response...</Typography>
          </Box>
        )}

        {isEmpty && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary"
            }}
          >
            <Typography>No messages yet. Start the conversation!</Typography>
          </Box>
        )}
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          fullWidth
          size="small"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          sx={{ backgroundColor: "#ffffff" }}
        />
        <Button
          variant="contained"
          onClick={sendMessage}
          disabled={sending || !input.trim()}
          startIcon={
            sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />
          }
          sx={{ backgroundColor: "#29C0BB", textTransform: "none" }}
        >
          {sending ? "Sending" : "Send"}
        </Button>
      </Stack>
    </Paper>
  );
};

export default AgentChatPanel;